/**
 * 页面完整性检查组件 (Refactored)
 * 职责: 检查各个站点的Listing要素是否完备，支持自定义项和数据持久化
 */

import React, { useState } from 'react';
import { Select, Progress, Typography, Button, Row, Col, Space, message, Spin } from 'antd';
import {
    CheckCircleOutlined, PlusOutlined, SaveOutlined
} from '@ant-design/icons';
import { useListingCheck } from './useListingCheck';
import { CategoryCard } from './components/CategoryCard';
import { AddItemModal } from './components/AddItemModal';
import { SITES, CATEGORY_ORDER } from './constants';
import type { ListingCheckProps } from './types';

const { Title, Text } = Typography;
const { Option } = Select;

export const ListingCheck: React.FC<ListingCheckProps> = ({ skuId }) => {
    const {
        activeSite,
        setActiveSite,
        loading,
        saving,
        progress,
        groupedResults,
        updateResult,
        saveResults,
        addItem,
    } = useListingCheck(skuId);

    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    // Split categories into two columns
    const leftCols = CATEGORY_ORDER.filter((_, i) => i % 2 === 0);
    const rightCols = CATEGORY_ORDER.filter((_, i) => i % 2 === 1);

    if (loading && !saving) { // Don't show full loading spinner when just saving
        // Optional: Initial loading state if desired, though usually skeleton or spinner is better
    }

    return (
        <div className="listing-check-container" style={{ padding: '0 12px' }}>
            {/* Header */}
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space size="large">
                    <Space>
                        <Text strong>站点:</Text>
                        <Select
                            value={activeSite}
                            onChange={setActiveSite}
                            style={{ width: 100 }}
                            size="middle"
                        >
                            {SITES.map(s => <Option key={s} value={s}>{s}</Option>)}
                        </Select>
                    </Space>

                    <div style={{ width: 300, display: 'flex', alignItems: 'center' }}>
                        <Text strong style={{ marginRight: 12, minWidth: 60 }}>页面体检:</Text>
                        <Progress
                            percent={progress}
                            size="default"
                            strokeColor={progress === 100 ? '#52c41a' : '#1890ff'}
                            format={percent => `${percent}%`}
                        />
                    </div>
                </Space>

                <Space>
                    <Button icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
                        添加检查项
                    </Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={saveResults}
                        loading={saving}
                    >
                        保存结果
                    </Button>
                </Space>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 50 }}>
                    <Spin tip="加载检查项..." />
                </div>
            ) : (
                <>
                    {/* Content: 2 Columns */}
                    <Row gutter={16}>
                        <Col span={12}>
                            {leftCols.map(cat => (
                                <CategoryCard
                                    key={cat}
                                    categoryKey={cat}
                                    items={groupedResults[cat] || []}
                                    onUpdateItem={updateResult}
                                />
                            ))}
                        </Col>
                        <Col span={12}>
                            {rightCols.map(cat => (
                                <CategoryCard
                                    key={cat}
                                    categoryKey={cat}
                                    items={groupedResults[cat] || []}
                                    onUpdateItem={updateResult}
                                />
                            ))}
                        </Col>
                    </Row>

                    {/* Success State */}
                    {progress === 100 && (
                        <div style={{ marginTop: 8, textAlign: 'center', padding: 24, background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                            <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 12 }} />
                            <Title level={4} style={{ margin: 0, color: '#389e0d' }}>完美！Listing要素已齐备</Title>
                            <Text type="secondary">当前站点 {activeSite} 的所有检查项均已达标。</Text>
                        </div>
                    )}
                </>
            )}

            {/* Add Item Modal */}
            <AddItemModal
                open={isAddModalVisible}
                onCancel={() => setIsAddModalVisible(false)}
                onAdd={addItem}
            />
        </div>
    );
};

